import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { subDays } from 'date-fns';
import { utcStartOfMonth, utcEndOfMonth, utcStartOfDay, utcEndOfDay, utcStartOfYear, utcStartOfPrevMonth, utcEndOfPrevMonth } from '@/lib/utc-date-utils';
import { loadActiveClients, isDemoClientId } from '@/components/new-ux/dashboard/_activeClients';
// 86ca7n8d4 (2026-06-11): this hook's ~29 debug logs fired on every
// dashboard load (twice), flooding the client console. Routed through the
// opt-in gate — silent in prod unless localStorage.ob_debug = '1'.
import { dlog } from '@/lib/debug-log';

interface ConversionFunnelData {
  emailsSent: number;
  totalReplies: number;
  humanReplies: number;
  positiveReplies: number;
  meetingsBooked: number;
  breakcoldMeetings: number;
  attributionMeetings: number;
  directMeetings: number; // Added
  directDetails: any[]; // Added
  attributionDetails: any[]; // Added
  qualifiedMeetings: number;
  breakcoldQualified: number;
  attributionQualified: number;
  directQualified: number; // Added for qualified meetings
  qualifiedDirectDetails: any[]; // Added for qualified meetings
  qualifiedAttributionDetails: any[]; // Added for qualified meetings
  salsQualified: number;
  dealsClosed: number;
  totalRevenue: number;
  replyRate: number;
  positiveReplyRate: number;
  meetingBookingRate: number;
  qualificationRate: number;
  leadsContacted: number;
  companiesContacted: number;
}

interface ConversionFunnelParams {
  timeFrame: string;
  clientId?: string;
  dateRange?: {
    from: Date;
    to: Date;
  };
  customDateRange?: {
    startDate: Date;
    endDate: Date;
  };
  /** Defaults to true. Pass false to defer the fetch — used by
   * useDashboardHero to keep the slow prior-period RPC off the
   * critical path until current-period data has resolved. */
  enabled?: boolean;
}

export const useConversionFunnel = ({ timeFrame, clientId, dateRange, customDateRange, enabled = true }: ConversionFunnelParams) => {
  return useQuery({
    enabled,
    queryKey: ['conversionFunnel', timeFrame, clientId, dateRange, 'v15-exact-meetings-management-logic'],
    queryFn: async (): Promise<ConversionFunnelData> => {
      // Note: Demo mode removed — always query real Supabase data regardless of SmartLead API key.
      // The SmartLead API key is a build-time env var that may not be present in all environments,
      // but the funnel data comes from Supabase tables (populated by edge functions), not the SmartLead API directly.

      dlog('🔍 Fetching conversion funnel data for:', { timeFrame, clientId, dateRange });
      
      // Resolve clientId to numeric client_id for RPC calls.
      // clientId can be: UUID (from clients.id), client name, numeric client_id, or 'all'.
      //
      // D-005 Phase 2 (Sabeeh, 2026-04-29): this used to fire 1-3 separate
      // queries against `clients` (active list, byName, byId) per call,
      // and useConversionFunnel runs twice per dashboard load (current
      // period + prior period for deltas) for 2-6 round-trips. Now we
      // call `loadActiveClients()` once — which is shared via React
      // Query's `['active-client-ids']` cache with the rest of the
      // dashboard — and resolve byName / byId in-memory from the
      // returned list. Net effect on a warm cache: 0 round-trips.
      const resolveClient = async (clientId: string | undefined): Promise<{ mappedId: string; allClientIds: number[] }> => {
        const { clients: activeClients } = await loadActiveClients();
        // All-clients aggregate must exclude sales-demo/sandbox clients
        // (Flowmail) so their fake/inflated numbers don't pollute team totals.
        // Per-client resolution below still reads the full list, so the demo's
        // own view resolves normally.
        const allClientIds = activeClients
          .filter((c) => !isDemoClientId(c.id) && !isDemoClientId(c.client_id))
          .map((c) => Number(c.client_id))
          .filter((id) => Number.isFinite(id));

        if (!clientId || clientId === 'all') {
          return { mappedId: 'all', allClientIds };
        }

        // Already numeric
        if (/^\d+$/.test(clientId)) {
          return { mappedId: clientId, allClientIds };
        }

        // Look up by name (in-memory)
        const byName = activeClients.find((c) => c.name === clientId);
        if (byName?.client_id) return { mappedId: byName.client_id, allClientIds };

        // Look up by UUID (in-memory)
        const byId = activeClients.find((c) => c.id === clientId);
        if (byId?.client_id) return { mappedId: byId.client_id, allClientIds };

        return { mappedId: 'all', allClientIds };
      };

      const { mappedId: mappedClientIdForRPC, allClientIds } = await resolveClient(clientId);
      
      dlog('🔍 Client ID mapping:', { 
        originalClientId: clientId, 
        mappedClientIdForRPC 
      });
      
      // Calculate date range based on timeFrame
      // All boundaries use UTC so they align with Postgres timestamps regardless of browser timezone.
      const now = new Date();
      let startDate: Date;
      let endDate: Date = now;

      switch (timeFrame) {
        case 'MTD':
          startDate = utcStartOfMonth(now);
          endDate = now; // MTD should only go up to now, not end of month
          break;
        case 'LAST_7_DAYS':
          startDate = subDays(now, 7);
          break;
        case 'LAST_30_DAYS':
          startDate = subDays(now, 30);
          break;
        case 'LAST_90_DAYS':
          startDate = subDays(now, 90);
          break;
        case 'LAST_MONTH':
        case 'Last Month': {
          startDate = utcStartOfPrevMonth(now);
          endDate = utcEndOfPrevMonth(now);
          break;
        }
        case 'YTD':
          startDate = utcStartOfYear(now);
          break;
        case 'LAST_QUARTER':
          startDate = subDays(now, 90);
          break;
        case 'LAST_6_MONTHS':
          startDate = subDays(now, 180);
          break;
        case 'LAST_1_YEAR':
          startDate = subDays(now, 365);
          break;
        case 'CUSTOM':
          if (dateRange?.from && dateRange?.to) {
            startDate = utcStartOfDay(dateRange.from);
            endDate = utcEndOfDay(dateRange.to);
          } else {
            startDate = subDays(now, 30);
          }
          break;
        default:
          startDate = subDays(now, 30);
      }

      dlog('📅 Date range calculated:', { 
        startDate: startDate.toISOString(), 
        endDate: endDate.toISOString(),
        clientId: clientId || 'all'
      });

      // Use Promise.allSettled to fetch all data concurrently and handle partial failures
      dlog('🔄 Fetching ALL conversion funnel data concurrently...');
      
      const [
        emailsResult,
        repliesResult,
        humanRepliesResult,
        positiveRepliesResult,
        directMeetingsResult,
        attributionMeetingsResult,
        directQualifiedResult,
        attributionQualifiedResult,
        dealsResult
      ] = await Promise.allSettled([
        // Emails sent data — D-053 (Sabeeh, 2026-05-06): switched to
        // the v2 RPC which reads `mv_obsync_email_sent_daily` (a
        // pre-aggregated daily matview, refreshed every 10min via
        // pg_cron). v1 had 17s cold-cache scans; v2 is ~150-700ms.
        // Hybrid: matview for full days + live table for partial-day
        // boundaries → exact parity with v1 across all chip windows.
        //
        // Sabeeh, 2026-09-02: now v3. Same body as v2 plus an additive
        // leg for clients on Instantly (Global-e), read from
        // `v_email_events`. The two legs are disjoint by client_id, so
        // v3 == v2 for every SmartLead client - verified against Cruva,
        // Duel, GFD and Flowmail over four date windows (whole month,
        // last 30d, last 7d, partial day) with both metrics identical,
        // and re-checked under a simulated client-role session so the
        // Instantly rows cannot leak cross-client.
        supabase.rpc('get_conversion_funnel_email_analytics_v3', {
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString(),
          target_client_id: mappedClientIdForRPC
        }),
        
        // Total replies data — unified RPC: masterinbox_replies (post Jan 2026) + obsync (pre-history)
        supabase.rpc('get_unified_replies_analytics', {
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString(),
          target_client_id: mappedClientIdForRPC
        }),
        
        // Human replies data - now will be derived from the RPC response
        Promise.resolve({ data: null, error: null }),

        // Positive replies data - now will be derived from the RPC response
        Promise.resolve({ data: null, error: null }),
        
        // Meetings booked data - Use EXACT same logic as Meetings Management Dashboard
        (async () => {
          dlog('🔍 Fetching meetings booked using EXACT Meetings Management logic');
          
          // Fetch from BOTH tables like Meetings Management Dashboard does
          let meetingsQuery = supabase.from('meetings_booked').select('*')
          let qualifiedQuery = supabase.from('qualified_meetings').select('*')
          
          // CRM overhaul Phase 4 (Sabeeh, 2026-04-29): the meetings_booked
          // / qualified_meetings tables are now thin Clay landing tables
          // (id, client_id text, crm_provider, crm_record_id, payload jsonb,
          // received_at). Old date columns (`meetings_booked_create_date`,
          // `qualified_meetings_create_date`) and old per-meeting fields
          // (`lead_email`, `direct_meeting` etc.) are gone. Filter by
          // `received_at` and treat client_id as TEXT. Detail-level
          // post-processing below produces empty direct/attribution
          // arrays for now — funnel COUNTS still work which is what the
          // dashboard cards consume.
          //
          // For the 'all' aggregate we pass allClientIds as TEXT (the
          // new column is text, not int).
          const allClientIdsText = allClientIds.map((id) => String(id))

          if (mappedClientIdForRPC === 'all') {
            meetingsQuery = meetingsQuery.in('client_id', allClientIdsText)
            qualifiedQuery = qualifiedQuery.in('client_id', allClientIdsText)
          } else {
            // Match as text against the new schema
            meetingsQuery = meetingsQuery.eq('client_id', mappedClientIdForRPC)
            qualifiedQuery = qualifiedQuery.eq('client_id', mappedClientIdForRPC)
          }

          // Date filter on the new column.
          meetingsQuery = meetingsQuery
            .gte('received_at', startDate.toISOString())
            .lte('received_at', endDate.toISOString())
            .order('received_at', { ascending: false })
            .limit(250)

          qualifiedQuery = qualifiedQuery
            .gte('received_at', startDate.toISOString())
            .lte('received_at', endDate.toISOString())
            .order('received_at', { ascending: false })
            .limit(250)
          
          const [meetingsResult, qualifiedResult] = await Promise.all([
            meetingsQuery,
            qualifiedQuery
          ])
          
          dlog('📊 Raw results:', {
            meetings_booked: meetingsResult.data?.length || 0,
            qualified_meetings: qualifiedResult.data?.length || 0,
            meetings_booked_data: meetingsResult.data,
            qualified_meetings_data: qualifiedResult.data
          });
          
          // Process meetings like Meetings Management Dashboard does
          const meetingsBookedData = (meetingsResult.data || []).map(meeting => ({
            ...meeting,
            table_source: 'meetings_booked',
            meeting_status: 'Meeting Booked',
            is_meeting_booked: true,
            is_qualified_meeting: false,
            source_type: meeting.direct_meeting ? 'direct' : 'attribution'
          }));
          
          const qualifiedMeetingsData = (qualifiedResult.data || []).map(meeting => ({
            ...meeting,
            table_source: 'qualified_meetings',
            meeting_status: 'Qualified Meeting',
            is_meeting_booked: false,
            is_qualified_meeting: true,
            source_type: meeting.direct_meeting ? 'direct' : 'attribution'
          }));
          
          // Combine both datasets with proper deduplication like Meetings Management Dashboard
          const allMeetings: any[] = [];
          const processedKeys = new Set<string>();
          
          // First, add all meetings_booked data as 'Meeting Booked'
          meetingsBookedData.forEach(meeting => {
            const key = `${meeting.lead_email}_${meeting.meetings_booked_create_date || meeting.created_at}`;
            allMeetings.push(meeting);
            processedKeys.add(key);
          });
          
          // Then, add qualified meetings, but if they're duplicates, upgrade the existing one to 'Qualified Meeting'
          qualifiedMeetingsData.forEach(qualifiedMeeting => {
            const key = `${qualifiedMeeting.lead_email}_${qualifiedMeeting.qualified_meetings_create_date || qualifiedMeeting.created_at}`;
            
            if (processedKeys.has(key)) {
              // Find the existing meeting and upgrade it to 'Qualified Meeting'
              const existingIndex = allMeetings.findIndex(m => 
                `${m.lead_email}_${m.meetings_booked_create_date || m.created_at}` === key
              );
              if (existingIndex !== -1) {
                allMeetings[existingIndex] = {
                  ...allMeetings[existingIndex],
                  ...qualifiedMeeting,
                  meeting_status: 'Qualified Meeting',
                  is_qualified_meeting: true,
                  is_meeting_booked: false
                };
                dlog('🔄 Upgraded meeting to Qualified:', qualifiedMeeting.lead_email);
              }
            } else {
              // Add as new qualified meeting
              allMeetings.push(qualifiedMeeting);
              processedKeys.add(key);
            }
          });
          
          // Separate by source type for meetings booked only
          const meetingsBookedOnly = allMeetings.filter(m => m.meeting_status === 'Meeting Booked');
          const directMeetings = meetingsBookedOnly.filter(m => m.source_type === 'direct');
          const attributionMeetings = meetingsBookedOnly.filter(m => m.source_type === 'attribution');
          
          dlog('🤝 Processed meetings data (EXACT Meetings Management logic):', {
            total_meetings: allMeetings.length,
            meetings_booked: meetingsBookedOnly.length,
            direct: directMeetings.length,
            attribution: attributionMeetings.length,
            qualified: allMeetings.filter(m => m.meeting_status === 'Qualified Meeting').length,
            meetings_booked_details: meetingsBookedOnly.map(m => ({
              id: m.id,
              direct_meeting: m.direct_meeting,
              source_type: m.source_type,
              lead_email: m.lead_email
            }))
          });
          
          return { data: allMeetings, error: null };
        })(),

        // Placeholder for removed attribution meetings RPC (now handled in meetings_booked table)
        Promise.resolve({ data: { attribution_meetings: 0, attribution_details: [] }, error: null }),
        
        // Qualified meetings data - First check lead_category_updates for direct qualified meetings
        (async () => {
          dlog('🔍 Fetching qualified meetings - starting with lead_category_updates for direct qualified');
          
          // First, get direct qualified meetings from lead_category_updates
          let directQualifiedQuery = supabase
            .from('lead_category_updates')
            .select('*')
            .or('category.eq.Qualified Meeting,lead_category_id.eq.63370')
            .gte('created_at', startDate.toISOString())
            .lte('created_at', endDate.toISOString());

          // Apply client filter for direct qualified meetings using mapped numeric client_id
          if (mappedClientIdForRPC === 'all') {
            dlog('🎯 Filtering direct qualified meetings by ALL clients: allClientIds');
            directQualifiedQuery = directQualifiedQuery.in('client_id', allClientIds);
          } else {
            const numericClientId = parseInt(mappedClientIdForRPC, 10);
            if (!isNaN(numericClientId)) {
              dlog('🎯 Filtering direct qualified meetings by single client:', numericClientId);
              directQualifiedQuery = directQualifiedQuery.eq('client_id', numericClientId);
            } else {
              dlog('🎯 Filtering direct qualified meetings by ALL clients (fallback): allClientIds');
              directQualifiedQuery = directQualifiedQuery.in('client_id', allClientIds);
            }
          }

          const directQualifiedResult = await directQualifiedQuery;
          dlog('🎯 Direct qualified meetings from lead_category_updates:', directQualifiedResult.data?.length || 0);
          
          // Phase 4: qualified_meetings is now the Clay landing table —
          // filter on `received_at` and treat client_id as TEXT.
          const allClientIdsTextQ = allClientIds.map((id) => String(id))
          let attributionQualifiedQuery = supabase
            .from('qualified_meetings')
            .select('*')
            .gte('received_at', startDate.toISOString())
            .lte('received_at', endDate.toISOString());

          if (mappedClientIdForRPC === 'all') {
            attributionQualifiedQuery = attributionQualifiedQuery.in('client_id', allClientIdsTextQ);
          } else {
            attributionQualifiedQuery = attributionQualifiedQuery.eq('client_id', mappedClientIdForRPC);
          }

          const attributionQualifiedResult = await attributionQualifiedQuery;
          dlog('📊 Attribution qualified meetings from qualified_meetings table:', attributionQualifiedResult.data?.length || 0);
          
          // Combine results and mark source types
          const allQualifiedMeetings = [
            ...(directQualifiedResult.data || []).map(m => ({ 
              ...m, 
              source_type: 'direct',
              email_domain: m.lead_email ? m.lead_email.split('@')[1] : '',
              qualified_meetings_create_date: m.created_at
            })),
            ...(attributionQualifiedResult.data || []).map(m => ({ 
              ...m, 
              source_type: 'attribution'
            }))
          ];
          
          dlog('✅ Combined qualified meetings data:', {
            direct: directQualifiedResult.data?.length || 0,
            attribution: attributionQualifiedResult.data?.length || 0,
            total: allQualifiedMeetings.length
          });
          
          return { data: allQualifiedMeetings, error: null };
        })(),

        // Remove attribution qualified RPC call since we're using unified qualified_meetings table
        Promise.resolve({ data: { attribution_qualified: 0, attribution_details: [] }, error: null }),

        // Deals data — D-003 lifted (Sabeeh, 2026-04-29). Reads from
        // the new deals_won Clay landing table. Empty until Clay starts
        // feeding data; revenue still 0 until we see real payload fields.
        (async () => {
          const allClientIdsTextD = allClientIds.map((id) => String(id))
          let q = supabase
            .from('deals_won')
            .select('id, payload, received_at')
            .gte('received_at', startDate.toISOString())
            .lte('received_at', endDate.toISOString())
            .limit(1000)
          if (mappedClientIdForRPC === 'all') {
            q = q.in('client_id', allClientIdsTextD)
          } else {
            q = q.eq('client_id', mappedClientIdForRPC)
          }
          const { data, error } = await q
          if (error) {
            console.error('❌ Error fetching deals_won', error)
            return { data: [], error }
          }
          return { data: data ?? [], error: null }
        })()
      ]);

      // Process emails data
      let emailsSent = 0;
      let leadsContactedFromRPC = 0;
      if (emailsResult.status === 'fulfilled' && !emailsResult.value.error) {
        const emailsData = emailsResult.value.data as any;
        emailsSent = emailsData?.emails_sent || 0;
        leadsContactedFromRPC = emailsData?.leads_contacted || 0;
        dlog('📧 Emails sent result:', emailsSent, 'leads:', leadsContactedFromRPC);
      } else {
        console.error('❌ Error fetching emails:', emailsResult.status === 'fulfilled' ? emailsResult.value.error : emailsResult.reason);
      }

      // Process replies data with breakdown
      let totalReplies = 0;
      let humanReplies = 0;
      let positiveRepliesFromRPC = 0;
      let neutralReplies = 0;
      let negativeReplies = 0;
      let oooReplies = 0;
      let autoReplies = 0;
      
      if (repliesResult.status === 'fulfilled' && !repliesResult.value.error) {
        const repliesData = repliesResult.value.data as any;
        dlog('🔍 RAW REPLIES RESULT:', {
          status: repliesResult.status,
          error: repliesResult.value.error,
          rawData: repliesData,
          dataType: typeof repliesData,
          keys: repliesData ? Object.keys(repliesData) : 'no data',
          totalRepliesValue: repliesData?.total_replies,
          humanRepliesValue: repliesData?.human_replies,
          positiveRepliesValue: repliesData?.positive_replies
        });
        
        // Debug each field extraction separately
        const extractedTotal = repliesData?.total_replies;
        const extractedHuman = repliesData?.human_replies;
        const extractedPositive = repliesData?.positive_replies;
        
        dlog('🔍 FIELD EXTRACTION DEBUG:', {
          extractedTotal,
          extractedHuman,
          extractedPositive,
          totalType: typeof extractedTotal,
          humanType: typeof extractedHuman,
          positiveType: typeof extractedPositive
        });
        
        totalReplies = extractedTotal || 0;
        humanReplies = extractedHuman || 0;
        positiveRepliesFromRPC = extractedPositive || 0;
        neutralReplies = repliesData?.neutral_replies || 0;
        negativeReplies = repliesData?.negative_replies || 0;
        oooReplies = repliesData?.ooo_replies || 0;
        autoReplies = repliesData?.auto_replies || 0;
        
        dlog('💬 DETAILED REPLIES DEBUG:', {
          repliesData,
          totalReplies,
          humanReplies,
          breakdown: {
            positive: positiveRepliesFromRPC,
            neutral: neutralReplies,
            negative: negativeReplies,
            ooo: oooReplies,
            auto: autoReplies
          },
          timeFrame,
          clientId,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        });
      } else {
        console.error('❌ Error fetching replies:', repliesResult.status === 'fulfilled' ? repliesResult.value.error : repliesResult.reason);
        dlog('❌ FAILED REPLIES RESULT:', {
          status: repliesResult.status,
          error: repliesResult.status === 'fulfilled' ? repliesResult.value.error : repliesResult.reason
        });
      }

      // Use positive replies from RPC response instead of separate query
      let positiveReplies = positiveRepliesFromRPC;
      dlog('✅ Positive replies from RPC:', positiveReplies);

      // Process meetings data with new logic: must have email sent, reply, and meeting booked status
      let directMeetingsCount = 0;
      let attributionMeetingsCount = 0;
      let directDetails: any[] = [];
      let attributionDetails: any[] = [];
      
      if (directMeetingsResult.status === 'fulfilled' && !directMeetingsResult.value.error) {
        const meetingsData = directMeetingsResult.value.data as any[];
        dlog('🔍 Processing meetings using EXACT Meetings Management logic...');
        
        dlog('📊 Meetings data loaded:', {
          meetingRecords: meetingsData?.length || 0
        });
        
        // Filter for meetings booked only (like Meetings Management Dashboard)
        const meetingsBookedOnly = meetingsData.filter(m => m.meeting_status === 'Meeting Booked');
        
        dlog('📊 Processing meetings booked only:', {
          totalMeetings: meetingsData.length,
          meetingsBooked: meetingsBookedOnly.length
        });
        
        // Separate direct and attribution meetings for meetings booked only
        const directMeetings = meetingsBookedOnly.filter(m => m.source_type === 'direct');
        const attributionMeetings = meetingsBookedOnly.filter(m => m.source_type === 'attribution');
        
        directMeetingsCount = directMeetings.length;
        attributionMeetingsCount = attributionMeetings.length;
        directDetails = directMeetings;
        attributionDetails = attributionMeetings;
        
        dlog('🎯 Processed meetings booked (EXACT Meetings Management logic):', {
          total: meetingsBookedOnly.length,
          direct: directMeetingsCount,
          attribution: attributionMeetingsCount,
          originalCount: meetingsData?.length || 0
        });
      }
      
      const breakcoldMeetings = directMeetingsCount;
      const attributionMeetings = attributionMeetingsCount;

      // Qualified meetings data - Use same logic as Meetings Management Dashboard
      let qualifiedMeetingsCount = 0;
      let qualifiedMeetingsDetails: any[] = [];
      let directQualifiedCount = 0;
      let attributionQualifiedCount = 0;
      let qualifiedDirectDetails: any[] = [];
      let qualifiedAttributionDetails: any[] = [];

      // Get qualified meetings from the same data source
      if (directMeetingsResult.status === 'fulfilled' && !directMeetingsResult.value.error) {
        const meetingsData = directMeetingsResult.value.data as any[];
        const qualifiedMeetingsOnly = meetingsData.filter(m => m.meeting_status === 'Qualified Meeting');
        
        const directQualified = qualifiedMeetingsOnly.filter(m => m.source_type === 'direct');
        const attributionQualified = qualifiedMeetingsOnly.filter(m => m.source_type === 'attribution');
        
        qualifiedMeetingsCount = qualifiedMeetingsOnly.length;
        directQualifiedCount = directQualified.length;
        attributionQualifiedCount = attributionQualified.length;
        qualifiedDirectDetails = directQualified;
        qualifiedAttributionDetails = attributionQualified;
        
        dlog('✅ Qualified meetings from same data source:', {
          total: qualifiedMeetingsCount,
          direct: directQualifiedCount,
          attribution: attributionQualifiedCount
        });
      }

      // Use qualified meetings counts
      const qualifiedMeetings = qualifiedMeetingsCount;
      const directQualified = directQualifiedCount;
      const attributionQualified = attributionQualifiedCount;
      const breakcoldQualified = directQualified;
      const salsQualified = 0;

      // Calculate total meetings
      const totalMeetingsBooked = directMeetingsCount + attributionMeetingsCount;
      const finalQualifiedMeetings = qualifiedMeetingsCount;

      dlog('🤝 Final meetings calculation:', {
        direct: directMeetingsCount,
        attribution: attributionMeetingsCount,
        total: totalMeetingsBooked
      });

      // Deals data — D-003 lifted. dealsClosed = COUNT of rows in
      // deals_won within the period. totalRevenue stays 0 for now —
      // we'll lift it once we see actual Clay payload fields and know
      // which key carries amount + currency.
      let dealsClosed = 0;
      const totalRevenue = 0;
      if (dealsResult.status === 'fulfilled' && !(dealsResult.value as any).error) {
        const rows = (dealsResult.value as any).data as any[] | undefined;
        dealsClosed = rows?.length ?? 0;
      }
      dlog('🏆 Deals data:', { dealsClosed, totalRevenue });


      // Real leads count from RPC (COUNT DISTINCT to_email)
      const leadsContacted = leadsContactedFromRPC || emailsSent;

      // Calculate rates with correct denominators:
      // reply rate = replies / leads contacted, human rate = human / total replies, positive = positive / human
      const replyRate = leadsContacted > 0 ? (totalReplies / leadsContacted) * 100 : 0;
      const positiveReplyRate = humanReplies > 0 ? (positiveReplies / humanReplies) * 100 : 0;
      const meetingBookingRate = positiveReplies > 0 ? (totalMeetingsBooked / positiveReplies) * 100 : 0;
      const qualificationRate = totalMeetingsBooked > 0 ? (finalQualifiedMeetings / totalMeetingsBooked) * 100 : 0;
      const companiesContacted = 0; // Not used in current UI

      const finalResult = {
        emailsSent,
        totalReplies,
        humanReplies,
        positiveReplies,
        meetingsBooked: totalMeetingsBooked,
        breakcoldMeetings,
        attributionMeetings,
        directMeetings: breakcoldMeetings, // Use breakcoldMeetings as directMeetings for backward compatibility
        directDetails,
        attributionDetails,
        qualifiedMeetings: finalQualifiedMeetings,
        breakcoldQualified,
        attributionQualified: attributionQualified,
        directQualified,
        qualifiedDirectDetails,
        qualifiedAttributionDetails,
        salsQualified,
        dealsClosed,
        totalRevenue,
        replyRate,
        positiveReplyRate,
        meetingBookingRate: positiveReplies > 0 ? (totalMeetingsBooked / positiveReplies) * 100 : 0,
        qualificationRate: totalMeetingsBooked > 0 ? (finalQualifiedMeetings / totalMeetingsBooked) * 100 : 0,
        leadsContacted,
        companiesContacted,
      };

      dlog('📊 Final conversion funnel result:', finalResult);
      return finalResult;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 10 * 60 * 1000, // 10 minutes
    // D-005: keep the previously-resolved client's data on screen during the
    // ~1s funnel refetch when the user switches client / period, instead of
    // flashing every cell to 0 and looking like the new selection has no data.
    placeholderData: keepPreviousData,
  });
};